import React, { useLayoutEffect, useRef } from "react";
import { useRecoilValue } from "recoil";

import css from "@emotion/css";
import styled from "@emotion/styled";

import Chord from "./Chord";
import ChordLine, { IChordLine } from "./ChordLine";
import chordSizeState from "../../../recoil/chordSizeState";
import notesPerLineState from "../../../recoil/notesPerLineState";

export interface INoteBar {
  chords: string[];
  lyrics: string;
  meter?: string; // 곡 중간에 박자가 바뀌는 마디 ('2/4') — meter.ts
  scale?: number; // 이 마디 길이 / 보통 마디 길이
}

interface INoteBarProps extends INoteBar, IChordLine {
  onClickNoteBar: (threshold?: number) => void;
  stopPlay: () => void;
  lyricsWidth: number;
  allChordCode: any[];
}

export default ({
  allChordCode,
  chords,
  lyrics,
  meter,
  progress,
  lyricsWidth,
  speedInSeconds,
  threshold,
  isEndOfLine = false,
  isEndOfNote = false,
  hideProgress = false,
  isTransitionDisabled = false,
  onClickNoteBar,
  stopPlay,
}: INoteBarProps) => {
  const notesPerLine = useRecoilValue(notesPerLineState);
  const chordSize = useRecoilValue(chordSizeState);
  const barRef = useRef<HTMLDivElement>(null);
  useFitChords(barRef, chords);

  return (
    <Wrapper line={notesPerLine} data-nb={(threshold || 0) / 100}>
      <Container ref={barRef} data-bar="" onClick={() => onClickNoteBar(threshold)}>
        {meter && <MeterBadge title={/\//.test(meter) ? `이 마디는 ${meter}박자` : meter}>{meter}</MeterBadge>}
        <ChordCodeContainer
          className="chord-code-container"
          size={chordSize}
          line={notesPerLine}
          lyricsWidth={lyricsWidth}
        >
          {chords.map((chord, chordIndex) => (
            <Chord key={`chord-${chordIndex}`} chord={chord} allChordCode={allChordCode} stopPlay={stopPlay}/>
          ))}
        </ChordCodeContainer>
        <ChordLine
          progress={progress}
          threshold={threshold}
          speedInSeconds={speedInSeconds}
          isEndOfLine={isEndOfLine}
          isEndOfNote={isEndOfNote}
          hideProgress={hideProgress}
          isTransitionDisabled={isTransitionDisabled}
        />
        <Lyrics
          width={lyricsWidth}
          size={chordSize}
          isFlexStart={lyrics?.length < 35}
        >
          {/* 마디 보기는 한글만 (2026-10-11 사장님: 글로벌은 가사 보기만, 로마자는 가사 보기에서) */}
          {lyrics && lyrics.trim() ? <BeatLyrics text={lyrics} /> : "ㅤ"}
        </Lyrics>
      </Container>
    </Wrapper>
  );
};

/**
 * 코드 칩이 다음 칩(또는 마디 끝)을 덮으면 그 칩만 줄인다 (2026-10-11 사장님 캡처: Fsus4 가 F 를 덮음).
 * 칩 안 글자 크기는 그대로 두고 transform 으로만 줄여 마디 배치는 바뀌지 않는다.
 */
const useFitChords = (ref: React.RefObject<HTMLDivElement>, deps: any) => {
  useLayoutEffect(() => {
    const bar = ref.current;
    if (!bar) return;
    const fit = () => {
      const chips = Array.from(bar.querySelectorAll(".chord-code-container var")) as HTMLElement[];
      const real = chips.filter((el) => (el.textContent || "").replace(/ㅤ/g, "").trim());
      const end = bar.getBoundingClientRect().right;
      real.forEach((el, i) => {
        const r = el.getBoundingClientRect();
        const cur = Number(el.dataset.fit || 1);
        const natural = r.width / cur;
        const limit = (i + 1 < real.length ? real[i + 1].getBoundingClientRect().left : end) - 2;
        const room = limit - r.left;
        const f = natural > room && room > 0 ? Math.max(0.55, Math.floor((room / natural) * 100) / 100) : 1;
        if (f !== cur) {
          el.dataset.fit = String(f);
          el.style.display = "inline-block";
          el.style.transformOrigin = "left center";
          el.style.transform = f < 1 ? `scale(${f})` : "";
        }
      });
    };
    fit();
    if (typeof (window as any).ResizeObserver === "undefined") {
      window.addEventListener("resize", fit);
      return () => window.removeEventListener("resize", fit);
    }
    const ro = new (window as any).ResizeObserver(fit);
    ro.observe(bar);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deps]);
};

/**
 * 마디 가사: 글자의 칸 위치(한 마디 36칸 = 박 위치)대로 놓는다 (2026-10-07 사장님: 음절이 마디 안 제 박에 있어야 함).
 * 예전엔 글자를 그냥 이어 붙여 빈칸이 좁게 그려져서 마디 끝 음절('또')이 가운데쯤으로 당겨졌다.
 * 칸 비율로 놓되, 글자끼리 겹치면 오른쪽으로 밀고, 마디 끝을 넘으면 왼쪽으로 당긴다.
 */
// 띄어쓰기 앞뒤가 둘 다 영어 같은 글자일 때만 단어 사이로 본다. 한글·일본어 가사의 빈칸은 대부분 박 자리를 맞추려고 넣은
// 칸이라 띄우면 '하 루 종 일' 처럼 벌어진다 (2026-10-12)
const CJK = /[ᄀ-ᇿ぀-ヿ㄰-㆏㐀-鿿가-힯]/;
const wordGap = (chars: string[], i: number) => {
  if (i === 0 || !/\s/.test(chars[i - 1])) return false;
  let j = i - 1;
  let nbsp = false;
  while (j >= 0 && /\s/.test(chars[j])) { if (chars[j] === " ") nbsp = true; j--; }
  if (j < 0) return false;
  // 한글 가사는 빈칸이 박자 자리 메우기라 띄어쓰기로 보지 않는다. 단 ' '(낱말 끝 표시)는 띄어쓰기
  // (2026-10-12 사장님 캡처 16725: '텐데 많이'가 '텐데많 이'로 붙어 보임)
  return nbsp || (!CJK.test(chars[i]) && !CJK.test(chars[j]));
};

const BeatLyrics: React.FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLDivElement>(null);
  const chars = [...text];
  const scale = Math.max(36, chars.length);
  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) return;
    const place = () => {
      // 가사 줄은 글자 크기용 zoom·scale 이 걸려 있어 마디 폭보다 좁게 잡힌다 → 마디(Container) 오른쪽 끝까지 쓰도록 폭을 맞춘다
      const host = box.closest("[data-bar]") as HTMLElement | null;
      const r = box.getBoundingClientRect();
      const k = box.offsetWidth ? r.width / box.offsetWidth : 1;
      if (host && k > 0) {
        const avail = host.getBoundingClientRect().right - r.left - 6 * k;
        // 폭을 바꾸면 ResizeObserver 가 다시 불러 소수점 차이로 계속 고쳐 쓰며 글씨가 떨렸다(2026-10-10 사장님 녹화) → 0.5px 넘게 다를 때만
        if (avail > 0 && Math.abs(avail / k - box.offsetWidth) > 0.5) box.style.width = `${avail / k}px`;
      }
      if (!box.clientWidth) return;
      // 가사 칸은 마디선에서 마디선까지 그대로 쓴다 (2026-10-13 사장님 "여백을 안 두면 안 되나?").
      // 예전엔 마디 양쪽에 여백이 있어 마디선을 넘어 이어 부르는 '들|어와'(16759 Flower)가 두 글자만큼 벌어져 보였다.
      // base = 가사 줄 왼쪽에서 마디선까지 거리(음수), W = 마디선 사이 폭. 첫 박 글자는 마디선 바로 뒤, 끝 글자는 다음 마디선 바로 앞까지 온다.
      const hostR = host ? host.getBoundingClientRect() : null;
      const base = hostR ? -(r.left - hostR.left) / k + 2 : 0;
      const W = hostR ? hostR.width / k - 4 : box.clientWidth;
      const els = Array.from(box.children) as HTMLElement[];
      const lefts = els.map((el) => base + (Number(el.dataset.col) / scale) * W);
      // 가사가 마디 폭보다 길면 글자가 왼쪽 끝에 겹쳐 쌓였다(2026-10-11 사장님 캡처) → 글자 폭을 좁혀(scaleX) 마디 안에 다 들어가게
      const raw = els.map((el) => el.offsetWidth);
      // 띄어쓰기 자리는 글자 사이를 조금 띄운다. 예전엔 빈칸을 아예 안 세서, 줄이면 영어 가사가
      // 'sayallthewordsinside' 처럼 다 붙어 보였다 (2026-10-12 품질 점검)
      const sp = (parseFloat(getComputedStyle(box).fontSize) || 14) * 0.5;
      const gp = els.map((el, i) => (i > 0 && el.dataset.sp ? sp : 0));
      const total = raw.reduce((a, b) => a + b, 0) + gp.reduce((a, b) => a + b, 0);
      const f = total > W ? Math.floor((W / total) * 100) / 100 : 1;
      const ws = raw.map((w) => w * f);
      const gs = gp.map((g) => g * f);
      for (let i = 1; i < els.length; i++) lefts[i] = Math.max(lefts[i], lefts[i - 1] + ws[i - 1] + gs[i]);
      for (let i = els.length - 1; i >= 0; i--) {
        const lim = (i === els.length - 1 ? base + W : lefts[i + 1] - gs[i + 1]) - ws[i];
        if (lefts[i] > lim) lefts[i] = Math.max(base, lim);
      }
      els.forEach((el, i) => {
        const x = Math.round(lefts[i] * 2) / 2; // 0.5px 단위로 맞춰 같은 값이면 다시 쓰지 않음
        if (el.style.left !== `${x}px`) el.style.left = `${x}px`;
        const t = f < 1 ? `scaleX(${f})` : "";
        if (el.style.transform !== t) {
          el.style.transform = t;
          el.style.transformOrigin = "left center";
        }
      });
    };
    place();
    if (typeof (window as any).ResizeObserver === "undefined") {
      window.addEventListener("resize", place);
      return () => window.removeEventListener("resize", place);
    }
    const ro = new (window as any).ResizeObserver(place);
    ro.observe(box);
    // 가사 줄 폭은 px 로 고정해 두므로 화면을 돌려 마디가 넓어져도 가사 줄은 그대로였다(2026-10-11 가로 보기에서 마디 뒤가 비어 보임) → 마디도 지켜봄
    const host = box.closest("[data-bar]");
    if (host) ro.observe(host);
    return () => ro.disconnect();
  }, [text, scale]);
  return (
    <BeatBox ref={ref} data-nt="">
      {chars.map((ch, i) =>
        /\s/.test(ch) ? null : (
          <span key={i} data-col={i} data-sp={wordGap(chars, i) ? "1" : undefined} style={{ left: `${(i / scale) * 100}%` }}>
            {ch}
          </span>
        )
      )}
    </BeatBox>
  );
};

const BeatBox = styled.div`
  position: relative;
  flex: none;
  width: 100%;
  height: 1.3em;
  letter-spacing: 0;
  & > span {
    position: absolute;
    top: 0;
    white-space: pre;
  }
`;

interface INoteWrapper {
  line: number;
}

interface INoteZoomDom {
  size: number;
}

const Wrapper = styled.div<INoteWrapper>`
  margin-bottom: 39px;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: 1300px) {
    margin-bottom: 28px;
  }

  @media screen and (max-width: 585px) {
    margin-bottom: 24px;
  }

  ${({ line }) =>
    line === 3
      ? css`
          width: calc(100% / 3);

          @media screen and (-ms-high-contrast: active),
            (-ms-high-contrast: none) {
            width: 33.333%;
          }
        `
      : css`
          width: calc(100% / ${line});
        `}
`;

const Container = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  cursor: pointer;
`;

// 곡 중간에 박자가 바뀌는 마디 표시 ('2/4') — 마디 왼쪽 위
const MeterBadge = styled.span`
  position: absolute;
  left: 0;
  top: -16px;
  z-index: 1;
  padding: 0 4px;
  height: 14px;
  line-height: 14px;
  border-radius: 4px;
  border: 1px solid #5b8cff;
  color: #8fb0ff;
  font-size: 10px;
  font-weight: 700;
  font-style: normal;
  background: #0d0e12;
`;

interface IChordCodeContainer extends INoteZoomDom, INoteWrapper {
  lyricsWidth: number;
}

const ChordCodeContainer = styled.div<IChordCodeContainer>`
  display: flex;
  /* width: 100%; */
  margin: 0 8px;
  margin-bottom: 12px;
  align-items: center;
  height: 15px;
  justify-content: space-between;

  @media screen and (max-width: 1300px) {
    margin-bottom: 10px;
  }

  @media screen and (max-width: 1075px) {
    margin: 0 6px;
    margin-bottom: 10px;
  }

  @media screen and (max-width: 685px) {
    margin: 0 5px;
    margin-bottom: 5px;
  }

  ${({ size }) =>
    size &&
    css`
      zoom: ${size};
    `}

  ${({ size }) =>
    size <= 1.5 &&
    css`
      & > var {
        @media screen and (max-width: 465px) {
          transform: scale(0.45);
        }

        @media screen and (max-width: 395px) {
          transform: scale(0.4);
        }

        @media screen and (max-width: 345px) {
          transform: scale(0.35);
        }
      }
    `};

  ${({ size }) =>
    size === 2 &&
    css`
      & > var {
        @media screen and (max-width: 1064px) {
          font-size: 11px;
          transform: scale(0.7);
          transform-origin: left;
        }

        @media screen and (max-width: 620px) {
          transform: scale(0.65);
        }

        @media screen and (max-width: 545px) {
          transform: scale(0.6);
        }

        @media screen and (max-width: 510px) {
          transform: scale(0.55);
        }

        @media screen and (max-width: 470px) {
          transform: scale(0.5);
        }

        @media screen and (max-width: 450px) {
          transform: scale(0.48);
        }

        @media screen and (max-width: 425px) {
          transform: scale(0.42);
        }

        @media screen and (max-width: 370px) {
          transform: scale(0.35);
        }
      }
    `};

  ${({ line }) =>
    line === 3 &&
    css`
      @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
        width: 60%;
      }
    `};

  /* 2026-10-10 사장님 승인(a-qa-barfont): 휴대폰 기본(2)에서 코드 칩을 약 25% 키움. 한 줄 2마디는 그대로 */
  ${({ size }) =>
    size === 2 &&
    css`
      /* 코드 칩 상자(Chord.tsx ChordCodeContainer)는 575px 이하에서 scale(0.55) → 0.69 */
      @media screen and (max-width: 480px) {
        & > div {
          transform: scale(0.69);
          transform-origin: left;
        }
      }
    `};
`;

interface ILyrics extends INoteZoomDom {
  width: number;
  isFlexStart: boolean;
}

const Lyrics = styled.div<ILyrics>`
  display: flex;
  align-self: stretch; /* 가사 칸 위치 계산에 마디 폭 전체가 필요 (BeatLyrics) */
  box-sizing: border-box;
  font-size: 13px;
  font-weight: 400;
  color: #1e1e1e;
  padding: 0 8px;
  margin-top: 8px;
  white-space: pre;
  height: 16px;

  /* 예전엔 max-width: lyricsWidth/2 로 줄였는데, 가사를 칸 위치로 놓으면서 마디 폭 전체를 쓴다 (2026-10-07) */

  ${({ isFlexStart, size }) =>
    isFlexStart
      ? css`
          justify-content: flex-start;

          ${size >= 1.5 &&
          css`
            & > span {
              margin-right: 0.5px;
            }
          `}

          ${size >= 2 &&
          css`
            & > span {
              margin-right: 1.5px;

              @media screen and (max-width: 1000px) {
                margin-right: 1.3px;
              }

              @media screen and (max-width: 930px) {
                margin-right: 1.2px;
              }

              @media screen and (max-width: 900px) {
                margin-right: 1.1px;
              }

              @media screen and (max-width: 880px) {
                margin-right: 0.9px;
              }

              @media screen and (max-width: 870px) {
                margin-right: 0.85px;
              }

              @media screen and (max-width: 850px) {
                margin-right: 0.7px;
              }

              @media screen and (max-width: 840px) {
                margin-right: 0.5px;
              }

              @media screen and (max-width: 800px) {
                margin-right: 1.5px;
              }
            }
          `}
        `
      : css`
          justify-content: space-between;

          @media screen and (max-width: 850px) {
            letter-spacing: -0.65px;
          }

          @media screen and (max-width: 845px) {
            letter-spacing: -0.75px;
          }

          @media screen and (max-width: 745px) {
            letter-spacing: -0.9px;
          }

          @media screen and (max-width: 730px) {
            letter-spacing: -1px;
          }
        `};

  html[color-mode="dark"] & {
    color: #ffffff;
  }

  .print & {
    color: #1e1e1e !important;
  }

  @media screen and (max-width: 1300px) {
    font-size: 13px;
  }

  @media screen and (max-width: 1075px) {
    font-size: 12px;
    height: 12px;
    padding: 0 6px;
    margin-top: 8px;
  }

  @media screen and (max-width: 708px) {
    font-weight: 400;
  }

  @media screen and (max-width: 685px) {
    font-size: 11px;
    height: 11px;
    padding: 0 5px;
    margin-top: 8px;
  }

  @media screen and (max-width: 608px) {
    padding: 0 5px;
    margin-top: 5px;
  }

  @media screen and (max-width: 585px) {
    font-size: 9px;
    ${({ size }) =>
      size <= 2
        ? css`
            transform: scale(0.7);
            transform-origin: left;
          `
        : css`
            & > span {
              transform: scale(0.7);
              transform-origin: left;
            }
          `}
  }

  ${({ size }) =>
    size &&
    css`
      zoom: ${size};
    `}

  ${({ size }) =>
    size >= 1.5 &&
    css`
      font-size: 13px;

      @media screen and (max-width: 1400px) {
        font-size: 12px;
      }

      @media screen and (max-width: 1100px) {
        font-size: 11px;
      }

      @media screen and (max-width: 980px) {
        font-size: 8px;
      }

      @media screen and (max-width: 512px) {
        letter-spacing: -1.6px;
      }
    `}

  ${({ size, isFlexStart }) =>
    size >= 2 &&
    (isFlexStart
      ? css`
          letter-spacing: 0.45px;

          @media screen and (max-width: 1300px) {
            letter-spacing: -0.9px;
          }

          @media screen and (max-width: 800px) {
            letter-spacing: -2.2px;
          }
        `
      : css`
          letter-spacing: -0.5px;
        `)}

  ${({ size, isFlexStart }) =>
    size === 2 &&
    (isFlexStart
      ? css`
          @media screen and (max-width: 685px) {
            letter-spacing: -2.3px;
          }

          @media screen and (max-width: 670px) {
            letter-spacing: -2.5px;
          }

          @media screen and (max-width: 655px) {
            letter-spacing: -2.7px;
          }

          @media screen and (max-width: 620px) {
            letter-spacing: -2.9px;
          }

          @media screen and (max-width: 605px) {
            letter-spacing: -3.1px;
          }

          @media screen and (max-width: 590px) {
            letter-spacing: -3.3px;
          }

          @media screen and (max-width: 585px) {
            letter-spacing: -1.1px;
          }

          @media screen and (max-width: 575px) {
            letter-spacing: -1.3px;
          }

          @media screen and (max-width: 565px) {
            letter-spacing: -1.4px;
          }

          @media screen and (max-width: 555px) {
            letter-spacing: -1.5px;
          }

          @media screen and (max-width: 540px) {
            letter-spacing: -1.7px;
          }

          @media screen and (max-width: 520px) {
            letter-spacing: -1.9px;
          }

          @media screen and (max-width: 500px) {
            letter-spacing: -2.1px;
          }

          @media screen and (max-width: 480px) {
            letter-spacing: -1.3px;
          }

          @media screen and (max-width: 460px) {
            letter-spacing: -1.5px;
          }

          @media screen and (max-width: 450px) {
            letter-spacing: -1.7px;
          }

          @media screen and (max-width: 440px) {
            letter-spacing: -2px;
          }

          @media screen and (max-width: 430px) {
            letter-spacing: -2.2px;
          }

          @media screen and (max-width: 420px) {
            letter-spacing: -2.4px;
          }

          @media screen and (max-width: 410px) {
            letter-spacing: -3.6px;
          }

          @media screen and (max-width: 400px) {
            letter-spacing: -3.8px;
          }

          @media screen and (max-width: 395px) {
            letter-spacing: -4px;
          }

          @media screen and (max-width: 385px) {
            // letter-spacing: -4px;
            letter-spacing: -2.2px;
            transform: scale(0.65);
          }

          @media screen and (max-width: 370px) {
            letter-spacing: -4.2px;
          }

          @media screen and (max-width: 360px) {
            letter-spacing: -4.4px;
          }

          @media screen and (max-width: 350px) {
            letter-spacing: -4.6px;
          }

          @media screen and (max-width: 340px) {
            letter-spacing: -4.8px;
          }

          @media screen and (max-width: 330px) {
            letter-spacing: -5.2px;
          }
        `
      : css`
          @media screen and (max-width: 685px) {
            letter-spacing: -0.6px;
          }

          @media screen and (max-width: 670px) {
            letter-spacing: -0.8px;
          }

          @media screen and (max-width: 655px) {
            letter-spacing: -1px;
          }

          @media screen and (max-width: 620px) {
            letter-spacing: -1.2px;
          }

          @media screen and (max-width: 605px) {
            letter-spacing: -1.4px;
          }

          @media screen and (max-width: 590px) {
            letter-spacing: -1.5px;
          }

          @media screen and (max-width: 585px) {
            letter-spacing: 0.2px;
          }

          @media screen and (max-width: 575px) {
            letter-spacing: 0.1px;
          }

          @media screen and (max-width: 565px) {
            letter-spacing: 0px;
          }

          @media screen and (max-width: 555px) {
            letter-spacing: -0.1px;
          }

          @media screen and (max-width: 540px) {
            letter-spacing: -0.2px;
          }

          @media screen and (max-width: 520px) {
            letter-spacing: -0.4px;
          }

          @media screen and (max-width: 500px) {
            letter-spacing: -0.6px;
          }

          @media screen and (max-width: 480px) {
            letter-spacing: 0px;
          }

          @media screen and (max-width: 465px) {
            letter-spacing: -0.1px;
          }

          @media screen and (max-width: 450px) {
            letter-spacing: -0.2px;
          }

          @media screen and (max-width: 440px) {
            letter-spacing: -0.3px;
          }

          @media screen and (max-width: 430px) {
            letter-spacing: -0.4px;
          }

          @media screen and (max-width: 420px) {
            letter-spacing: -0.5px;
          }

          @media screen and (max-width: 410px) {
            letter-spacing: -0.6px;
          }

          @media screen and (max-width: 400px) {
            letter-spacing: -0.6px;
          }

          @media screen and (max-width: 385px) {
            transform: scale(0.65);
            letter-spacing: -0.6px;
          }

          @media screen and (max-width: 370px) {
            letter-spacing: -1.4px;
          }

          @media screen and (max-width: 360px) {
            letter-spacing: -1.5px;
          }

          @media screen and (max-width: 350px) {
            letter-spacing: -1.6px;
          }

          @media screen and (max-width: 340px) {
            letter-spacing: -1.7px;
          }
        `)}

  ${({ size, isFlexStart }) =>
    size >= 2.5 &&
    (isFlexStart
      ? css`
          letter-spacing: 1px;
        `
      : css`
          letter-spacing: 1.5px;
        `)}

  ${({ size }) =>
    size < 2 &&
    css`
      letter-spacing: 0.3px;

      @media screen and (max-width: 1400px) {
        // letter-spacing: 0px;
        letter-spacing: 1px;
      }

      @media screen and (max-width: 1320px) {
        letter-spacing: -0.5px;
      }

      @media screen and (max-width: 1200px) {
        letter-spacing: -1.5px;
      }

      @media screen and (max-width: 1075px) {
        letter-spacing: 0;
      }

      @media screen and (max-width: 1050px) {
        letter-spacing: -0.8px;
      }

      @media screen and (max-width: 908px) {
        letter-spacing: -1.5px;
      }

      @media screen and (max-width: 708px) {
        letter-spacing: -1.8px;
      }

      @media screen and (max-width: 780px) {
        letter-spacing: -2px;
      }

      @media screen and (max-width: 685px) {
        letter-spacing: -2px;
      }

      @media screen and (max-width: 608px) {
        letter-spacing: -2px;
      }

      @media screen and (max-width: 585px) {
        letter-spacing: -0.5px;
      }

      @media screen and (max-width: 490px) {
        letter-spacing: -0.6px;
      }

      @media screen and (max-width: 480px) {
        letter-spacing: -0.7px;
      }

      @media screen and (max-width: 470px) {
        letter-spacing: -0.8px;
      }

      @media screen and (max-width: 460px) {
        letter-spacing: -0.9px;
      }

      @media screen and (max-width: 435px) {
        letter-spacing: -1.6px;
      }

      @media screen and (max-width: 395px) {
        letter-spacing: -1.9px;
      }

      @media screen and (max-width: 370px) {
        letter-spacing: -2px;
      }

      @media screen and (max-width: 360px) {
        letter-spacing: -2.2px;
      }

      @media screen and (max-width: 340px) {
        letter-spacing: -2.4px;
      }

      @media screen and (max-width: 330px) {
        letter-spacing: -2.6px;
      }
    `}

    ${({ size, isFlexStart }) =>
    size === 1.5
      ? isFlexStart
        ? css`
            @media screen and (max-width: 660px) {
              letter-spacing: -2.2px;
            }

            @media screen and (max-width: 650px) {
              letter-spacing: -2.4px;
            }

            @media screen and (max-width: 615px) {
              letter-spacing: -2.6px;
            }

            @media screen and (max-width: 585px) {
              letter-spacing: -1px;
            }

            @media screen and (max-width: 575px) {
              letter-spacing: -1.2px;
            }

            @media screen and (max-width: 555px) {
              letter-spacing: -1.4px;
            }

            @media screen and (max-width: 535px) {
              letter-spacing: -1.6px;
            }

            @media screen and (max-width: 500px) {
              letter-spacing: -1.8px;
            }

            @media screen and (max-width: 485px) {
              letter-spacing: -2px;
            }

            @media screen and (max-width: 465px) {
              letter-spacing: -2.2px;
            }

            @media screen and (max-width: 435px) {
              letter-spacing: -2.4px;
            }

            @media screen and (max-width: 425px) {
              letter-spacing: -2.6px;
            }

            @media screen and (max-width: 415px) {
              letter-spacing: -2.8px;
            }

            @media screen and (max-width: 385px) {
              letter-spacing: -3px;
            }

            @media screen and (max-width: 375px) {
              letter-spacing: -3.2px;
            }

            @media screen and (max-width: 370px) {
              letter-spacing: -3.4px;
            }

            @media screen and (max-width: 365px) {
              letter-spacing: -3.6px;
            }

            @media screen and (max-width: 360px) {
              letter-spacing: -3.8px;
            }

            @media screen and (max-width: 355px) {
              letter-spacing: -4px;
            }
          `
        : css`
            @media screen and (max-width: 545px) {
              letter-spacing: -0.6px;
            }

            @media screen and (max-width: 545px) {
              letter-spacing: -0.7px;
            }

            @media screen and (max-width: 515px) {
              letter-spacing: -0.8px;
            }

            @media screen and (max-width: 535px) {
              letter-spacing: -0.9px;
            }

            @media screen and (max-width: 500px) {
              letter-spacing: -1px;
            }

            @media screen and (max-width: 495px) {
              letter-spacing: -1.2px;
            }

            @media screen and (max-width: 470px) {
              letter-spacing: -1.3px;
            }

            @media screen and (max-width: 465px) {
              letter-spacing: -1.4px;
            }

            @media screen and (max-width: 460px) {
              letter-spacing: -1.5px;
            }

            @media screen and (max-width: 455px) {
              letter-spacing: -1.6px;
            }

            @media screen and (max-width: 435px) {
              letter-spacing: -1.7px;
            }

            @media screen and (max-width: 414px) {
              letter-spacing: -1.8px;
            }

            @media screen and (max-width: 395px) {
              letter-spacing: -2px;
            }

            @media screen and (max-width: 375px) {
              letter-spacing: -2.2px;
            }

            @media screen and (max-width: 365px) {
              letter-spacing: -2.4px;
            }
          `
      : ""};

  ${({ size, isFlexStart }) =>
    size === 2.5 &&
    (isFlexStart
      ? css`
          letter-spacing: 6.05px;

          @media screen and (max-width: 1430px) {
            letter-spacing: 5.91px;
          }

          @media screen and (max-width: 1400px) {
            letter-spacing: 6.3px;
          }

          @media screen and (max-width: 1380px) {
            letter-spacing: 6px;
          }

          @media screen and (max-width: 1355px) {
            letter-spacing: 5.8px;
          }

          @media screen and (max-width: 1335px) {
            letter-spacing: 5.5px;
          }

          @media screen and (max-width: 1320px) {
            letter-spacing: 5.6px;
          }

          @media screen and (max-width: 1310px) {
            letter-spacing: 5.4px;
          }

          @media screen and (max-width: 1300px) {
            letter-spacing: 4px;
          }

          @media screen and (max-width: 1295px) {
            letter-spacing: 3.8px;
          }

          @media screen and (max-width: 1290px) {
            letter-spacing: 3.9px;
          }

          @media screen and (max-width: 1280px) {
            letter-spacing: 3.6px;
          }

          @media screen and (max-width: 1270px) {
            letter-spacing: 3.4px;
          }

          @media screen and (max-width: 1250px) {
            letter-spacing: 3.2px;
          }

          @media screen and (max-width: 1230px) {
            letter-spacing: 3px;
          }

          @media screen and (max-width: 1210px) {
            letter-spacing: 2.8px;
          }

          @media screen and (max-width: 1200px) {
            letter-spacing: 4.4px;
          }

          @media screen and (max-width: 1180px) {
            letter-spacing: 4.2px;
          }

          @media screen and (max-width: 1160px) {
            letter-spacing: 4px;
          }

          @media screen and (max-width: 1140px) {
            letter-spacing: 3.8px;
          }

          @media screen and (max-width: 1120px) {
            letter-spacing: 3.6px;
          }

          @media screen and (max-width: 1110px) {
            letter-spacing: 3.4px;
          }

          @media screen and (max-width: 1100px) {
            letter-spacing: 3.7px;
          }

          @media screen and (max-width: 1070px) {
            letter-spacing: 3.4px;
          }

          @media screen and (max-width: 1060px) {
            letter-spacing: 3px;
          }

          @media screen and (max-width: 1050px) {
            letter-spacing: 2.9px;
          }

          @media screen and (max-width: 1040px) {
            letter-spacing: 2.7px;
          }

          @media screen and (max-width: 915px) {
            letter-spacing: 2.5px;
          }

          @media screen and (max-width: 865px) {
            letter-spacing: 2.3px;
          }

          @media screen and (max-width: 820px) {
            letter-spacing: 2.1px;
          }

          @media screen and (max-width: 805px) {
            letter-spacing: 1.9px;
          }

          @media screen and (max-width: 800px) {
            letter-spacing: 1px;
          }

          @media screen and (max-width: 790px) {
            letter-spacing: 0.7px;
          }

          @media screen and (max-width: 768px) {
            letter-spacing: 1.4px;
          }

          @media screen and (max-width: 750px) {
            letter-spacing: 1.3px;
          }

          @media screen and (max-width: 735px) {
            letter-spacing: 1px;
          }

          @media screen and (max-width: 715px) {
            letter-spacing: 0.7px;
          }

          @media screen and (max-width: 690px) {
            letter-spacing: 0.5px;
          }

          @media screen and (max-width: 670px) {
            letter-spacing: 0.2px;
          }

          @media screen and (max-width: 645px) {
            letter-spacing: 0;
          }

          @media screen and (max-width: 625px) {
            letter-spacing: -0.2px;
          }

          @media screen and (max-width: 610px) {
            letter-spacing: -0.5px;
          }

          @media screen and (max-width: 600px) {
            letter-spacing: -0.7px;
          }

          @media screen and (max-width: 590px) {
            letter-spacing: -0.55px;
          }

          @media screen and (max-width: 585px) {
            letter-spacing: -0.4px;
          }

          @media screen and (max-width: 570px) {
            letter-spacing: -0.5px;
          }

          @media screen and (max-width: 560px) {
            letter-spacing: -0.7px;
          }

          @media screen and (max-width: 550px) {
            letter-spacing: -0.8px;
          }

          @media screen and (max-width: 540px) {
            letter-spacing: -1px;
          }

          @media screen and (max-width: 530px) {
            letter-spacing: -1.2px;
          }

          @media screen and (max-width: 520px) {
            letter-spacing: -1.4px;
          }

          @media screen and (max-width: 500px) {
            letter-spacing: -1.5px;
          }

          @media screen and (max-width: 490px) {
            letter-spacing: -1.6px;
          }

          @media screen and (max-width: 475px) {
            letter-spacing: -1.8px;
          }

          @media screen and (max-width: 465px) {
            letter-spacing: -2px;
          }

          @media screen and (max-width: 445px) {
            letter-spacing: -2.2px;
          }

          @media screen and (max-width: 425px) {
            letter-spacing: -2.4px;
          }

          @media screen and (max-width: 405px) {
            letter-spacing: -2.6px;
          }

          @media screen and (max-width: 395px) {
            letter-spacing: -2.8px;
          }

          @media screen and (max-width: 385px) {
            letter-spacing: -3px;
          }

          @media screen and (max-width: 375px) {
            letter-spacing: -3.2px;
          }

          @media screen and (max-width: 365px) {
            letter-spacing: -3.4px;
          }

          @media screen and (max-width: 355px) {
            letter-spacing: -3.6px;
          }

          @media screen and (max-width: 345px) {
            letter-spacing: -3.8px;
          }

          @media screen and (max-width: 335px) {
            letter-spacing: -4.2px;
          }

          @media screen and (max-width: 325px) {
            letter-spacing: -4.4px;
          }

          @media screen and (max-width: 315px) {
            letter-spacing: -4.6px;
          }
        `
      : css`
          @media screen and (max-width: 1300px) {
            letter-spacing: -1px;
          }

          @media screen and (max-width: 610px) {
            letter-spacing: -1.5px;
          }

          @media screen and (max-width: 520px) {
            letter-spacing: -1.8px;
          }

          @media screen and (max-width: 480px) {
            letter-spacing: -2px;
          }

          @media screen and (max-width: 450px) {
            letter-spacing: -2.2px;
          }

          @media screen and (max-width: 420px) {
            letter-spacing: -2.4px;
          }

          @media screen and (max-width: 395px) {
            letter-spacing: -2.6px;
          }

          @media screen and (max-width: 370px) {
            letter-spacing: -2.8px;
          }

          @media screen and (max-width: 340px) {
            letter-spacing: -3px;
          }

          @media screen and (max-width: 330px) {
            letter-spacing: -3.2px;
          }

          @media screen and (max-width: 325px) {
            letter-spacing: -3.5px;
          }
        `)};

  ${({ size, isFlexStart }) =>
    size === 3 &&
    (isFlexStart
      ? css`
          letter-spacing: 3.4px;

          @media screen and (max-width: 1340px) {
            letter-spacing: 3.4px;
          }

          @media screen and (max-width: 1320px) {
            letter-spacing: 3px;
          }

          @media screen and (max-width: 1300px) {
            letter-spacing: 1.8px;
          }

          @media screen and (max-width: 1290px) {
            letter-spacing: 1.6px;
          }

          @media screen and (max-width: 1270px) {
            letter-spacing: 1.4px;
          }

          @media screen and (max-width: 1240px) {
            letter-spacing: 1.2px;
          }

          @media screen and (max-width: 1200px) {
            letter-spacing: 2.3px;
          }

          @media screen and (max-width: 1180px) {
            letter-spacing: 2.1px;
          }

          @media screen and (max-width: 1160px) {
            letter-spacing: 1.9px;
          }

          @media screen and (max-width: 1140px) {
            letter-spacing: 1.7px;
          }

          @media screen and (max-width: 1120px) {
            letter-spacing: 1.5px;
          }

          @media screen and (max-width: 1100px) {
            letter-spacing: 1.7px;
          }

          @media screen and (max-width: 1080px) {
            letter-spacing: 1.8px;
          }

          @media screen and (max-width: 1060px) {
            letter-spacing: 1.6px;
          }

          @media screen and (max-width: 1040px) {
            letter-spacing: 1.4px;
          }

          @media screen and (max-width: 1030px) {
            letter-spacing: 1.1px;
          }

          @media screen and (max-width: 980px) {
            letter-spacing: 1.4px;
          }

          @media screen and (max-width: 960px) {
            letter-spacing: 1.2px;
          }

          @media screen and (max-width: 920px) {
            letter-spacing: 0.8px;
          }

          @media screen and (max-width: 815px) {
            letter-spacing: 0.6px;
          }

          @media screen and (max-width: 800px) {
            letter-spacing: -0.4px;
          }

          @media screen and (max-width: 780px) {
            letter-spacing: -0.6px;
          }

          @media screen and (max-width: 768px) {
            letter-spacing: 0px;
          }

          @media screen and (max-width: 738px) {
            letter-spacing: -0.2px;
          }

          @media screen and (max-width: 728px) {
            letter-spacing: -0.4px;
          }

          @media screen and (max-width: 718px) {
            letter-spacing: -0.6px;
          }

          @media screen and (max-width: 680px) {
            letter-spacing: -0.8px;
          }

          @media screen and (max-width: 665px) {
            letter-spacing: -1px;
          }

          @media screen and (max-width: 645px) {
            letter-spacing: -1.2px;
          }

          @media screen and (max-width: 625px) {
            letter-spacing: -1.4px;
          }

          @media screen and (max-width: 600px) {
            letter-spacing: -1.6px;
          }

          @media screen and (max-width: 560px) {
            letter-spacing: -1.8px;
          }

          @media screen and (max-width: 540px) {
            letter-spacing: -2px;
          }

          @media screen and (max-width: 520px) {
            letter-spacing: -2.2px;
          }

          @media screen and (max-width: 500px) {
            letter-spacing: -2.4px;
          }

          @media screen and (max-width: 490px) {
            letter-spacing: -2.6px;
          }

          @media screen and (max-width: 470px) {
            letter-spacing: -2.8px;
          }

          @media screen and (max-width: 460px) {
            letter-spacing: -3px;
          }

          @media screen and (max-width: 440px) {
            letter-spacing: -3.2px;
          }

          @media screen and (max-width: 430px) {
            letter-spacing: -3.4px;
          }

          @media screen and (max-width: 420px) {
            letter-spacing: -3.6px;
          }

          @media screen and (max-width: 410px) {
            letter-spacing: -3.8px;
          }

          @media screen and (max-width: 400px) {
            letter-spacing: -4.2px;
          }

          @media screen and (max-width: 390px) {
            letter-spacing: -4.4px;
          }

          @media screen and (max-width: 380px) {
            letter-spacing: -4.6px;
          }

          @media screen and (max-width: 375px) {
            letter-spacing: -4.9px;
          }

          @media screen and (max-width: 370px) {
            letter-spacing: -5.2px;
          }

          @media screen and (max-width: 360px) {
            letter-spacing: -5.5px;
          }

          @media screen and (max-width: 350px) {
            letter-spacing: -5.7px;
          }

          @media screen and (max-width: 345px) {
            letter-spacing: -6px;
          }

          @media screen and (max-width: 335px) {
            letter-spacing: -6.5px;
          }
        `
      : css`
          @media screen and (max-width: 1300px) {
            letter-spacing: -1px;
          }

          @media screen and (max-width: 610px) {
            letter-spacing: -1.5px;
          }

          @media screen and (max-width: 520px) {
            letter-spacing: -1.8px;
          }

          @media screen and (max-width: 480px) {
            letter-spacing: -2px;
          }

          @media screen and (max-width: 450px) {
            letter-spacing: -2.2px;
          }

          @media screen and (max-width: 420px) {
            letter-spacing: -2.4px;
          }

          @media screen and (max-width: 395px) {
            letter-spacing: -2.6px;
          }

          @media screen and (max-width: 370px) {
            letter-spacing: -2.8px;
          }

          @media screen and (max-width: 340px) {
            letter-spacing: -3px;
          }

          @media screen and (max-width: 330px) {
            letter-spacing: -3.2px;
          }

          @media screen and (max-width: 325px) {
            letter-spacing: -3.5px;
          }
        `)};

  ${({ size, isFlexStart }) =>
    size === 3.5 &&
    (isFlexStart
      ? css`
          letter-spacing: 1.7px;

          @media screen and (max-width: 1300px) {
            letter-spacing: 0.3px;
          }

          @media screen and (max-width: 1050px) {
            letter-spacing: 0.1px;
          }

          @media screen and (max-width: 1030px) {
            letter-spacing: 0px;
          }

          @media screen and (max-width: 830px) {
            letter-spacing: -0.2px;
          }

          @media screen and (max-width: 800px) {
            letter-spacing: -1.4px;
          }

          @media screen and (max-width: 780px) {
            letter-spacing: -1.6px;
          }

          @media screen and (max-width: 768px) {
            letter-spacing: -1px;
          }

          @media screen and (max-width: 755px) {
            letter-spacing: -1.2px;
          }

          @media screen and (max-width: 715px) {
            letter-spacing: -1.4px;
          }

          @media screen and (max-width: 695px) {
            letter-spacing: -1.6px;
          }

          @media screen and (max-width: 670px) {
            letter-spacing: -1.8px;
          }

          @media screen and (max-width: 655px) {
            letter-spacing: -2px;
          }

          @media screen and (max-width: 620px) {
            letter-spacing: -2.2px;
          }

          @media screen and (max-width: 600px) {
            letter-spacing: -2.4px;
          }

          @media screen and (max-width: 580px) {
            letter-spacing: -2.4px;
          }

          @media screen and (max-width: 560px) {
            letter-spacing: -2.6px;
          }

          @media screen and (max-width: 530px) {
            letter-spacing: -2.8px;
          }

          @media screen and (max-width: 510px) {
            letter-spacing: -3.2px;
          }

          @media screen and (max-width: 500px) {
            letter-spacing: -3.4px;
          }

          @media screen and (max-width: 495px) {
            letter-spacing: -3.6px;
          }

          @media screen and (max-width: 485px) {
            letter-spacing: -3.8px;
          }

          @media screen and (max-width: 480px) {
            letter-spacing: -4.2px;
          }

          @media screen and (max-width: 470px) {
            letter-spacing: -4.4px;
          }

          @media screen and (max-width: 450px) {
            letter-spacing: -4.8px;
          }

          @media screen and (max-width: 435px) {
            letter-spacing: -5.2px;
          }

          @media screen and (max-width: 415px) {
            letter-spacing: -5.4px;
          }

          @media screen and (max-width: 405px) {
            letter-spacing: -5.6px;
          }

          @media screen and (max-width: 495px) {
            letter-spacing: -5.8px;
          }

          @media screen and (max-width: 485px) {
            letter-spacing: -6px;
          }

          @media screen and (max-width: 475px) {
            letter-spacing: -6.2px;
          }

          @media screen and (max-width: 465px) {
            letter-spacing: -6.4px;
          }

          @media screen and (max-width: 365px) {
            letter-spacing: -6.6px;
          }

          @media screen and (max-width: 350px) {
            letter-spacing: -7px;
          }

          @media screen and (max-width: 330px) {
            letter-spacing: -7.2px;
          }

          @media screen and (max-width: 322px) {
            letter-spacing: -7.4px;
          }
        `
      : css`
          @media screen and (max-width: 1300px) {
            letter-spacing: -1px;
          }

          @media screen and (max-width: 610px) {
            letter-spacing: -1.5px;
          }

          @media screen and (max-width: 520px) {
            letter-spacing: -1.8px;
          }

          @media screen and (max-width: 480px) {
            letter-spacing: -2px;
          }

          @media screen and (max-width: 450px) {
            letter-spacing: -2.2px;
          }

          @media screen and (max-width: 420px) {
            letter-spacing: -2.4px;
          }

          @media screen and (max-width: 395px) {
            letter-spacing: -2.6px;
          }

          @media screen and (max-width: 370px) {
            letter-spacing: -2.8px;
          }

          @media screen and (max-width: 340px) {
            letter-spacing: -3px;
          }

          @media screen and (max-width: 330px) {
            letter-spacing: -3.2px;
          }

          @media screen and (max-width: 325px) {
            letter-spacing: -3.5px;
          }
        `)};

  ${({ size, isFlexStart }) =>
    size === 4 &&
    (isFlexStart
      ? css`
          letter-spacing: 0.4px;

          @media screen and (max-width: 1320px) {
            letter-spacing: 0.2px;
          }

          @media screen and (max-width: 1300px) {
            letter-spacing: -0.8px;
          }

          @media screen and (max-width: 1280px) {
            letter-spacing: -1px;
          }

          @media screen and (max-width: 1100px) {
            letter-spacing: 0.5px;
          }

          @media screen and (max-width: 1080px) {
            letter-spacing: 0.3px;
          }

          @media screen and (max-width: 1050px) {
            letter-spacing: 0.1px;
          }

          @media screen and (max-width: 1030px) {
            letter-spacing: 0px;
          }

          @media screen and (max-width: 1020px) {
            letter-spacing: -1px;
          }

          @media screen and (max-width: 980px) {
            letter-spacing: -0.5px;
          }

          @media screen and (max-width: 970px) {
            letter-spacing: -0.6px;
          }

          @media screen and (max-width: 950px) {
            letter-spacing: -0.8px;
          }

          @media screen and (max-width: 920px) {
            letter-spacing: -1px;
          }

          @media screen and (max-width: 850px) {
            letter-spacing: -1.1px;
          }

          @media screen and (max-width: 800px) {
            letter-spacing: -2px;
          }

          @media screen and (max-width: 795px) {
            letter-spacing: -2.2px;
          }

          @media screen and (max-width: 770px) {
            letter-spacing: -1px;
          }

          @media screen and (max-width: 760px) {
            letter-spacing: -1.8px;
          }

          @media screen and (max-width: 740px) {
            letter-spacing: -2px;
          }

          @media screen and (max-width: 715px) {
            letter-spacing: -2.2px;
          }

          @media screen and (max-width: 680px) {
            letter-spacing: -2.4px;
          }

          @media screen and (max-width: 655px) {
            letter-spacing: -2.6px;
          }

          @media screen and (max-width: 625px) {
            letter-spacing: -2.8px;
          }

          @media screen and (max-width: 570px) {
            letter-spacing: -3px;
          }

          @media screen and (max-width: 565px) {
            letter-spacing: -3.5px;
          }
          @media screen and (max-width: 545px) {
            letter-spacing: -3.8px;
          }
          @media screen and (max-width: 535px) {
            letter-spacing: -4.1px;
          }

          @media screen and (max-width: 515px) {
            letter-spacing: -4.3px;
          }

          @media screen and (max-width: 500px) {
            letter-spacing: -4.5px;
          }

          @media screen and (max-width: 495px) {
            letter-spacing: -4.8px;
          }

          @media screen and (max-width: 485px) {
            letter-spacing: -5.1px;
          }
          @media screen and (max-width: 465px) {
            letter-spacing: -5.4px;
          }
          @media screen and (max-width: 455px) {
            letter-spacing: -5.8px;
          }

          @media screen and (max-width: 440px) {
            letter-spacing: -6.5px;
          }

          @media screen and (max-width: 410px) {
            letter-spacing: -6.8px;
          }

          @media screen and (max-width: 395px) {
            letter-spacing: -7px;
          }

          @media screen and (max-width: 378px) {
            letter-spacing: -7.2px;
          }

          @media screen and (max-width: 370px) {
            letter-spacing: -7.4px;
          }

          @media screen and (max-width: 365px) {
            letter-spacing: -7.6px;
          }

          @media screen and (max-width: 350px) {
            letter-spacing: -7.8px;
          }

          @media screen and (max-width: 345px) {
            letter-spacing: -8px;
          }

          @media screen and (max-width: 336px) {
            letter-spacing: -8.2px;
          }
        `
      : css`
          letter-spacing: 1px;

          @media screen and (max-width: 1300px) {
            letter-spacing: 0.3px;
          }

          @media screen and (max-width: 1300px) {
            letter-spacing: 0.1px;
          }

          @media screen and (max-width: 1280px) {
            letter-spacing: 0;
          }

          @media screen and (max-width: 975px) {
            letter-spacing: 0.4px;
          }

          @media screen and (max-width: 940px) {
            letter-spacing: 0.2px;
          }

          @media screen and (max-width: 910px) {
            letter-spacing: 0;
          }

          @media screen and (max-width: 860px) {
            letter-spacing: -0.5px;
          }

          @media screen and (max-width: 670px) {
            letter-spacing: -0.8px;
          }

          @media screen and (max-width: 620px) {
            letter-spacing: -1.1px;
          }

          @media screen and (max-width: 540px) {
            letter-spacing: -1.3px;
          }

          @media screen and (max-width: 500px) {
            letter-spacing: -1.5px;
          }

          @media screen and (max-width: 470px) {
            letter-spacing: -1.8px;
          }

          @media screen and (max-width: 410px) {
            letter-spacing: -2px;
          }

          @media screen and (max-width: 380px) {
            letter-spacing: -2.2px;
          }

          @media screen and (max-width: 350px) {
            letter-spacing: -2.3px;
          }

          @media screen and (max-width: 340px) {
            letter-spacing: -2.4px;
          }
        `)};

  /* 2026-10-10 사장님 승인(a-qa-barfont): 휴대폰 기본(2)에서 가사 글씨 약 25% 키움 (0.7 → 0.875). 칸 위치는 BeatLyrics 가 다시 맞춤 */
  ${({ size }) =>
    size === 2 &&
    css`
      @media screen and (max-width: 480px) {
        transform: scale(0.875);
        transform-origin: left;
      }
    `}
`;
